import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Globe, Heart, Users, TrendingUp, RefreshCw, Flag, AlertCircle } from 'lucide-react';

interface GlobalActivity {
  id: string;
  latitude: number;
  longitude: number;
  city?: string;
  country?: string;
  place_name?: string;
  activities: string[];
  time_of_day?: string;
  emoji?: string;
  color?: string;
  created_at: string;
  likes: number;
}

interface GlobalStats {
  totalShares: number;
  uniqueCountries: number;
  sharesToday: number;
  topCities: Array<{ city: string; country: string; count: number }>;
}

export default function GlobalActivityMap() {
  const [activities, setActivities] = useState<GlobalActivity[]>([]);
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<GlobalActivity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [flagTarget, setFlagTarget] = useState<GlobalActivity | null>(null);

  // Fetch activities
  const fetchActivities = async () => {
    try {
      const [activitiesRes, statsRes] = await Promise.all([
        fetch('https://biig-api.b-9f2.workers.dev/api/global/recent'),
        fetch('https://biig-api.b-9f2.workers.dev/api/global/stats')
      ]);

      const activitiesData = await activitiesRes.json();
      const statsData = await statsRes.json();

      if (activitiesData.success) {
        setActivities(activitiesData.activities);
      }
      if (statsData.success) {
        setStats(statsData.stats);
      }
    } catch (error) {
      console.error('Failed to fetch global activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    
    // Auto-refresh every 30 seconds
    if (autoRefresh) {
      const interval = setInterval(fetchActivities, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Generate session ID for likes
  const getSessionId = () => {
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  };

  const handleLike = async (activityId: string) => {
    try {
      const response = await fetch(`https://biig-api.b-9f2.workers.dev/api/global/like/${activityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: getSessionId() })
      });

      if (response.ok) {
        // Update local state
        setActivities(prev => 
          prev.map(a => a.id === activityId ? { ...a, likes: a.likes + 1 } : a)
        );
      }
    } catch (error) {
      console.error('Failed to like activity:', error);
    }
  };

  const handleFlag = async (activityId: string, flagType: string, reason?: string) => {
    try {
      const response = await fetch(`https://biig-api.b-9f2.workers.dev/api/global/flag/${activityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          sessionId: getSessionId(),
          flagType,
          reason
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(result.message);
        setShowFlagDialog(false);
        setFlagTarget(null);
        // Refresh to potentially hide flagged content
        fetchActivities();
      } else {
        alert(result.message || 'Failed to flag activity');
      }
    } catch (error) {
      console.error('Failed to flag activity:', error);
      alert('Failed to flag activity. Please try again.');
    }
  };

  const getTimeAgo = (timestamp: string) => {
    // Handle UTC timestamp from database (append Z if not present)
    const utcTimestamp = timestamp.includes('Z') || timestamp.includes('+') 
      ? timestamp 
      : timestamp + 'Z';
    
    const diff = Date.now() - new Date(utcTimestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <Globe className="w-10 h-10" />
            Global Activity Pulse
          </h1>
          <p className="text-purple-200 text-lg mb-4">
            See what people are discovering around the world right now
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/share?share=true"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur text-white font-medium rounded-lg hover:bg-white/30 transition-all duration-200"
            >
              <MapPin className="w-4 h-4" />
              Share Your Discovery
            </Link>
            <Link
              to="/share"
              className="inline-flex items-center gap-2 px-4 py-2 text-white/80 hover:text-white transition-colors"
            >
              ← Back to Share
            </Link>
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.totalShares.toLocaleString()}</div>
              <div className="text-purple-200 text-sm">Total Discoveries</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.uniqueCountries}</div>
              <div className="text-purple-200 text-sm">Countries</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.sharesToday}</div>
              <div className="text-purple-200 text-sm">Today</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 mx-auto ${
                  autoRefresh ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Live' : 'Paused'}
              </button>
            </div>
          </div>
        )}

        {/* Activity Feed */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className="bg-white/10 backdrop-blur rounded-xl p-6 hover:bg-white/20 transition-all cursor-pointer"
              onClick={() => setSelectedActivity(activity)}
            >
              {/* Location Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{activity.emoji || '📍'}</span>
                  <div>
                    <div className="font-bold text-white">
                      {activity.city || 'Unknown City'}
                    </div>
                    <div className="text-sm text-purple-200">
                      {activity.country || 'Somewhere'}
                    </div>
                  </div>
                </div>
                <div 
                  className="text-xs text-purple-300"
                  title={new Date(activity.created_at + (activity.created_at.includes('Z') ? '' : 'Z')).toLocaleString()}
                >
                  {getTimeAgo(activity.created_at)}
                </div>
              </div>

              {/* Activities Preview */}
              <div className="space-y-2 mb-4">
                {activity.activities.slice(0, 2).map((act, idx) => (
                  <div key={idx} className="text-sm text-gray-200">
                    <span className="text-yellow-400 mr-1">•</span>
                    {act.length > 60 ? act.substring(0, 60) + '...' : act}
                  </div>
                ))}
                {activity.activities.length > 2 && (
                  <div className="text-xs text-purple-300">
                    +{activity.activities.length - 2} more activities
                  </div>
                )}
              </div>

              {/* Engagement */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(activity.id);
                    }}
                    className="flex items-center gap-1 text-pink-400 hover:text-pink-300 transition-colors"
                  >
                    <Heart className="w-4 h-4" />
                    <span className="text-sm">{activity.likes}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFlagTarget(activity);
                      setShowFlagDialog(true);
                    }}
                    className="flex items-center gap-1 text-gray-400 hover:text-orange-400 transition-colors"
                    title="Flag as duplicate or inappropriate"
                  >
                    <Flag className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-1 text-purple-300">
                  <MapPin className="w-3 h-3" />
                  <span className="text-xs">
                    {activity.latitude.toFixed(2)}, {activity.longitude.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {!isLoading && activities.length === 0 && (
          <div className="text-center py-12">
            <Globe className="w-16 h-16 text-purple-400 mx-auto mb-4 animate-pulse" />
            <p className="text-xl text-purple-200 mb-6">
              Be the first to share an activity today!
            </p>
            <Link
              to="/share?share=true"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-xl"
            >
              <MapPin className="w-5 h-5" />
              Discover & Share Activities
            </Link>
            <p className="text-sm text-purple-300 mt-4">
              Get AI-powered activity recommendations for your location
            </p>
          </div>
        )}

        {/* Selected Activity Modal */}
        {selectedActivity && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedActivity(null)}
          >
            <div 
              className="bg-gradient-to-br from-purple-800 to-pink-800 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-2xl">{selectedActivity.emoji || '📍'}</span>
                  {selectedActivity.city || 'Unknown Location'}
                </h3>
                <button
                  onClick={() => setSelectedActivity(null)}
                  className="text-white/60 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <div className="text-sm text-purple-200 mb-2">
                  {selectedActivity.country} • {selectedActivity.time_of_day || 'anytime'}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-white">Activities discovered:</h4>
                {selectedActivity.activities.map((act, idx) => (
                  <div key={idx} className="bg-white/10 rounded-lg p-3">
                    <span className="text-yellow-400 font-bold mr-2">{idx + 1}.</span>
                    <span className="text-gray-200">{act}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-white/20">
                <div className="flex items-center justify-between text-sm text-purple-200">
                  <span title={new Date(selectedActivity.created_at + (selectedActivity.created_at.includes('Z') ? '' : 'Z')).toLocaleString()}>
                    {getTimeAgo(selectedActivity.created_at)}
                  </span>
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    <span>{selectedActivity.likes} likes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Cities */}
        {stats && stats.topCities.length > 0 && (
          <div className="mt-8 bg-white/10 backdrop-blur rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Trending Locations
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {stats.topCities.map((city, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-2xl mb-1">🏆</div>
                  <div className="font-semibold text-white">{city.city}</div>
                  <div className="text-xs text-purple-300">{city.country}</div>
                  <div className="text-sm text-purple-200">{city.count} shares</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Flag Dialog */}
        {showFlagDialog && flagTarget && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowFlagDialog(false);
              setFlagTarget(null);
            }}
          >
            <div 
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-6 h-6 text-orange-400" />
                <h3 className="text-xl font-bold text-white">Report Activity</h3>
              </div>

              <div className="mb-4">
                <p className="text-gray-300 mb-2">
                  Why are you flagging this activity from {flagTarget.city || 'this location'}?
                </p>
              </div>

              <div className="space-y-2 mb-6">
                <button
                  onClick={() => handleFlag(flagTarget.id, 'duplicate')}
                  className="w-full text-left px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                >
                  <span className="font-semibold">Duplicate</span>
                  <span className="block text-sm text-gray-400">Same location shared multiple times</span>
                </button>
                
                <button
                  onClick={() => handleFlag(flagTarget.id, 'inappropriate')}
                  className="w-full text-left px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                >
                  <span className="font-semibold">Inappropriate</span>
                  <span className="block text-sm text-gray-400">Contains offensive or harmful content</span>
                </button>
                
                <button
                  onClick={() => handleFlag(flagTarget.id, 'spam')}
                  className="w-full text-left px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                >
                  <span className="font-semibold">Spam</span>
                  <span className="block text-sm text-gray-400">Promotional or irrelevant content</span>
                </button>
                
                <button
                  onClick={() => {
                    const reason = prompt('Please describe the issue:');
                    if (reason) {
                      handleFlag(flagTarget.id, 'other', reason);
                    }
                  }}
                  className="w-full text-left px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                >
                  <span className="font-semibold">Other</span>
                  <span className="block text-sm text-gray-400">Different reason</span>
                </button>
              </div>

              <button
                onClick={() => {
                  setShowFlagDialog(false);
                  setFlagTarget(null);
                }}
                className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}